/**
 * Loads raw MDX content for blog posts via Vite's import.meta.glob.
 *
 * What: Exposes cached synchronous and async accessors for blog post body by
 * content path.
 * Why: The active blog article must be available before hydration without
 * eagerly bundling every raw MDX file into the route chunk.
 * When: Update when MDX paths, glob pattern, or test-mode handling change.
 */

type ContentLoader = () => Promise<string>;
type ContentLoaderMap = Record<string, ContentLoader>;

const SHOULD_USE_GLOB = import.meta.env.MODE !== 'test';

const CONTENT_LOADERS: ContentLoaderMap = SHOULD_USE_GLOB
  ? (import.meta.glob('/src/content/blog/posts/*.mdx', {
      query: '?raw',
      import: 'default',
    }) as ContentLoaderMap)
  : {};

const PRELOADED_BLOG_POST_CONTENT = new Map<string, string>();
const IN_FLIGHT_BLOG_POST_CONTENT = new Map<string, Promise<string>>();

/**
 * Resolve the glob loader for a given repository content path (e.g.
 * "src/content/blog/posts/foo.mdx"). Vite's glob keys may use a leading slash
 * or different path separators.
 */
function getBlogPostContentLoader(
  contentFilePath: string
): ContentLoader | null {
  if (!contentFilePath || typeof contentFilePath !== 'string') {
    return null;
  }

  const pathWithSlash = `/${contentFilePath}`;
  const pathWithoutSlash = contentFilePath;
  let loader: ContentLoader | undefined =
    CONTENT_LOADERS[pathWithSlash] ?? CONTENT_LOADERS[pathWithoutSlash];

  if (!loader) {
    const fileName = contentFilePath.split('/').pop();
    if (fileName) {
      const key = Object.keys(CONTENT_LOADERS).find(k => k.endsWith(fileName));
      loader = key ? CONTENT_LOADERS[key] : undefined;
    }
  }

  return loader ?? null;
}

function normalizeRawBlogPostContent(loadedContent: unknown): string {
  if (typeof loadedContent === 'string') {
    return loadedContent;
  }

  if (
    loadedContent &&
    typeof loadedContent === 'object' &&
    typeof (loadedContent as { default?: unknown }).default === 'string'
  ) {
    return (loadedContent as { default: string }).default;
  }

  return '';
}

function getCachedBlogPostContent(contentFilePath: string): string {
  if (!contentFilePath || typeof contentFilePath !== 'string') {
    return '';
  }

  return PRELOADED_BLOG_POST_CONTENT.get(contentFilePath) ?? '';
}

/**
 * Read raw MDX content synchronously when it has already been preloaded.
 */
export function getPreloadedBlogPostContent(contentFilePath: string): string {
  return getCachedBlogPostContent(contentFilePath);
}

/**
 * Preload raw MDX content into the cache for hydration-safe first paint.
 */
export async function preloadRawBlogPostContent(
  contentFilePath: string
): Promise<string> {
  if (!contentFilePath || typeof contentFilePath !== 'string') {
    return '';
  }

  if (PRELOADED_BLOG_POST_CONTENT.has(contentFilePath)) {
    return getCachedBlogPostContent(contentFilePath);
  }

  if (IN_FLIGHT_BLOG_POST_CONTENT.has(contentFilePath)) {
    return IN_FLIGHT_BLOG_POST_CONTENT.get(contentFilePath)!;
  }

  const loader = getBlogPostContentLoader(contentFilePath);
  if (!loader) {
    return '';
  }

  const loadingPromise = Promise.resolve(loader())
    .then(loadedContent => {
      const normalizedContent = normalizeRawBlogPostContent(loadedContent);
      PRELOADED_BLOG_POST_CONTENT.set(contentFilePath, normalizedContent);
      return normalizedContent;
    })
    .finally(() => {
      IN_FLIGHT_BLOG_POST_CONTENT.delete(contentFilePath);
    });

  IN_FLIGHT_BLOG_POST_CONTENT.set(contentFilePath, loadingPromise);
  return loadingPromise;
}

/**
 * Load raw MDX content for a post. Returns empty string when content is unavailable.
 */
export async function loadRawBlogPostContent(
  contentFilePath: string
): Promise<string> {
  return preloadRawBlogPostContent(contentFilePath);
}
